/**
 * WhyPage - Refactored with EME narrative and decision removal emphasis
 */

import { Check, X, Layers, Brain, Target, ArrowRight } from "lucide-react";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { Section } from "@/components/marketing/Section";
import { Container } from "@/components/marketing/Container";
import { Split } from "@/components/marketing/Split";
import { FeatureGrid } from "@/components/marketing/FeatureGrid";
import { ConversionCTA } from "@/components/marketing/ConversionCTA";
import { ScrollReveal } from "@/components/landing/ScrollReveal";
import { redirectToStripeCheckout } from "@/lib/stripe";
import { Button } from "@/components/ui/button";
import emeLogo from "@/assets/eme-logo.svg";

export default function WhyPage() {
  return (
    <MarketingLayout showBackLink>
      {/* Hero */}
      <Section spacing="lg">
        <Container size="lg">
          <ScrollReveal>
            <h1 className="text-[var(--font-display)] font-semibold tracking-[var(--tracking-tighter)] mb-6 leading-[var(--leading-display)]">
              Why Decision Sales OS?
            </h1>
            <p className="text-[var(--font-body-lg)] text-muted-foreground max-w-2xl leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
              The philosophy behind a new approach to sales execution.
            </p>
          </ScrollReveal>
        </Container>
      </Section>

      {/* Built from Real Execution - EME Narrative */}
      <Section variant="secondary" spacing="lg">
        <Container size="xl">
          <Split gap="xl" align="center">
            <div>
              <ScrollReveal>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                  <Target className="w-4 h-4" />
                  Built from Real Execution
                </div>
                <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-6 leading-[var(--leading-headline)]">
                  Born Inside EME
                </h2>
                <p className="text-[var(--font-body-lg)] text-muted-foreground mb-6 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                  Decision Sales OS wasn't built in a lab. It was built from real sales execution inside EME, where we faced the same problems you face: too many leads, too little time, too many decisions.
                </p>
                <p className="text-[var(--font-body-lg)] text-muted-foreground mb-8 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                  We built this system to remove bad decisions from our daily workflow. Now we're sharing it with teams who value execution over options.
                </p>
                <ul className="space-y-3">
                  {[
                    "Built from real sales execution challenges",
                    "Tested daily by our own sales team",
                    "Refined through actual revenue outcomes",
                    "Designed for professionals, not managers"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-foreground/80 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">{item}</span>
                    </li>
                  ))}
                </ul>
              </ScrollReveal>
            </div>
            <ScrollReveal delay={0.2}>
              <div className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/[0.08] to-primary/[0.02] p-8 text-center">
                <img 
                  src={emeLogo} 
                  alt="EME" 
                  className="h-12 mx-auto mb-4 opacity-80"
                />
                <p className="text-sm text-muted-foreground leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                  Built from real execution inside EME
                </p>
              </div>
            </ScrollReveal>
          </Split>
        </Container>
      </Section>

      {/* The Problem */}
      <Section spacing="lg">
        <Container size="lg">
          <ScrollReveal>
            <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-8 text-center leading-[var(--leading-headline)]">
              The Problem with CRMs
            </h2>
            <p className="text-[var(--font-body-lg)] text-muted-foreground text-center mb-12 max-w-2xl mx-auto leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
              CRMs were built for managers who want to track activities. They weren't built for salespeople who need to execute.
            </p>
          </ScrollReveal>
          
          <FeatureGrid cols={2} gap="md">
            {[
              "Too much data, no clear direction",
              "Constant manual prioritization",
              "Features built for reporting, not selling",
              "Hours spent on admin instead of calls",
              "Analysis paralysis every morning",
              "Reps hate using it, data stays dirty"
            ].map((problem, i) => (
              <ScrollReveal key={i} delay={i * 0.05}>
                <div className="flex items-start gap-4 p-5 rounded-xl bg-card border">
                  <X className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
                  <span className="text-foreground/80 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">{problem}</span>
                </div>
              </ScrollReveal>
            ))}
          </FeatureGrid>
        </Container>
      </Section>

      {/* vs CRM */}
      <Section variant="secondary" spacing="lg">
        <Container size="xl">
          <ScrollReveal>
            <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-12 text-center leading-[var(--leading-headline)]">
              Decision Sales OS vs Traditional CRM
            </h2>
          </ScrollReveal>
          
          <FeatureGrid cols={2} gap="lg">
            <ScrollReveal delay={0.1}>
              <div className="p-8 rounded-2xl border border-border/60 bg-card h-full">
                <div className="text-muted-foreground text-sm font-medium mb-6 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-muted-foreground/30" />
                  Traditional CRM
                </div>
                <div className="space-y-6">
                  {[
                    { title: "Purpose", desc: "Store and organize customer data" },
                    { title: "Primary user", desc: "Managers & analysts" },
                    { title: "Daily workflow", desc: "Log activities, run reports" },
                    { title: "Key question", desc: "What happened?" },
                    { title: "Outcome", desc: "Data for forecasting" }
                  ].map((item, i) => (
                    <div key={i}>
                      <div className="font-medium text-sm text-muted-foreground mb-1 tracking-[var(--tracking-normal)]">{item.title}</div>
                      <div className="text-foreground/80 leading-[var(--leading-body)]">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={0.2}>
              <div className="p-8 rounded-2xl border border-primary/30 bg-primary/5 h-full">
                <div className="text-primary text-sm font-medium mb-6 flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-primary" />
                  Decision Sales OS
                </div>
                <div className="space-y-6">
                  {[
                    { title: "Purpose", desc: "Tell you what to do today" },
                    { title: "Primary user", desc: "Sales reps executing daily" },
                    { title: "Daily workflow", desc: "Open Today view, execute actions" },
                    { title: "Key question", desc: "What should I do next?" },
                    { title: "Outcome", desc: "Revenue closed faster" }
                  ].map((item, i) => (
                    <div key={i}>
                      <div className="font-medium text-sm text-primary mb-1 tracking-[var(--tracking-normal)]">{item.title}</div>
                      <div className="text-foreground leading-[var(--leading-body)]">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </ScrollReveal>
          </FeatureGrid>
        </Container>
      </Section>

      {/* Decision Removal Narrative */}
      <Section spacing="lg">
        <Container size="lg">
          <ScrollReveal>
            <div className="text-center mb-16">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Layers className="w-4 h-4" />
                The Decision Layer
              </div>
              <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-6 leading-[var(--leading-headline)]">
                Removing Decisions, Not Adding Features
              </h2>
              <p className="text-[var(--font-body-lg)] text-muted-foreground max-w-2xl mx-auto leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">
                Decision Sales OS sits between your data and your actions. It transforms information into decisions—so you don't have to.
              </p>
            </div>
          </ScrollReveal>
          
          <ScrollReveal delay={0.1}>
            <div className="relative">
              <div className="space-y-4">
                <div className="p-6 rounded-xl bg-secondary/50 text-center">
                  <div className="text-sm text-muted-foreground mb-2">Your Data Layer</div>
                  <div className="font-medium">CRM, spreadsheets, emails, calls</div>
                </div>
                <div className="flex justify-center">
                  <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" />
                </div>
                <div className="p-6 rounded-xl bg-primary/10 border border-primary/30 text-center">
                  <Brain className="w-8 h-8 text-primary mx-auto mb-3" />
                  <div className="text-sm text-primary mb-2 font-medium">Decision Layer</div>
                  <div className="font-semibold text-lg tracking-[var(--tracking-tight)]">Decision Sales OS</div>
                  <p className="text-sm text-muted-foreground mt-2 leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">Removes decisions, surfaces actions</p>
                </div>
                <div className="flex justify-center">
                  <ArrowRight className="w-6 h-6 text-muted-foreground rotate-90" />
                </div>
                <div className="p-6 rounded-xl bg-success/10 border border-success/30 text-center">
                  <Target className="w-8 h-8 text-success mx-auto mb-3" />
                  <div className="text-sm text-success mb-2 font-medium">Action Layer</div>
                  <div className="font-semibold tracking-[var(--tracking-tight)]">You execute with confidence</div>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </Container>
      </Section>

      {/* Principles */}
      <Section variant="secondary" spacing="lg">
        <Container size="lg">
          <ScrollReveal>
            <h2 className="text-[var(--font-h2)] font-semibold tracking-[var(--tracking-tighter)] mb-12 text-center leading-[var(--leading-headline)]">
              Our Principles
            </h2>
          </ScrollReveal>
          
          <FeatureGrid cols={2} gap="md">
            {[
              {
                title: "Decisions over data",
                desc: "We don't show you more data. We tell you what it means and what to do about it."
              },
              {
                title: "Execution over planning",
                desc: "The best plan is worthless without execution. We optimize for action, not analysis."
              },
              {
                title: "Revenue over activity",
                desc: "We measure success by deals closed, not activities logged."
              },
              {
                title: "Simplicity over features",
                desc: "Every feature must reduce friction, not add it. We say no to complexity."
              }
            ].map((principle, i) => (
              <ScrollReveal key={i} delay={i * 0.1}>
                <div className="p-6 rounded-xl bg-card border border-border/60 h-full">
                  <h3 className="font-semibold mb-2 tracking-[var(--tracking-tight)] leading-[var(--leading-headline)]">{principle.title}</h3>
                  <p className="text-muted-foreground text-sm leading-[var(--leading-body)] tracking-[var(--tracking-normal)]">{principle.desc}</p>
                </div>
              </ScrollReveal>
            ))}
          </FeatureGrid>
        </Container>
      </Section>

      {/* CTA */}
      <ConversionCTA 
        headline="Experience the Difference"
        bullets={[
          'Try Decision Sales OS free for 14 days',
          'See how it transforms your daily workflow',
        ]}
      />
    </MarketingLayout>
  );
}

